import type { CascadeColumn, CascadeStack } from './types';

// ---------------------------------------------------------------------------
// Cascade tree ↔ URL codec (ADR-0027, Decision A: parent-index tags).
//
// Grammar — depth-first pre-order segments joined by `/`:
//
//   root:      type:id[@tab]
//   non-root:  type:id[@tab]^<parentIndex>[$sourceListKey]
//
// The root segment carries no `^` tag; every other segment carries `^<n>`,
// the flat index of its parent segment (always an earlier segment). The
// `(parent, sourceListKey)` pair is the slot identity the reducer keys on, so
// the slot key rides in the same tag group as `$key` (omitted when absent).
// Default (empty/undefined) tab is omitted. The `:new` draft id is a plain id
// and round-trips unchanged.
//
// Delimiters (`/ : @ ^ $`) are all escaped by `encodeURIComponent`, so they
// never collide with encoded type/id/tab/key values, which makes parsing
// unambiguous by first-delimiter scanning.
//
// Migration: an old flat link (ADR-0021, no `^` tags anywhere) is read as a
// single linear spine — each segment's parent is the one before it.
// ---------------------------------------------------------------------------

const SEGMENT_SEPARATOR = '/';
const TYPE_ID_SEPARATOR = ':';
const TAB_SEPARATOR = '@';
const PARENT_SEPARATOR = '^';
const SLOT_SEPARATOR = '$';

/**
 * Guards against pathological URLs (hand-edited, proxy-truncated, fuzzed). A
 * run beyond {@link MAX_SEGMENTS} columns, or a single segment longer than
 * {@link MAX_SEGMENT_LENGTH}, stops the parse gracefully — the longest valid
 * prefix is returned, never a throw.
 */
const MAX_SEGMENTS = 64;
const MAX_SEGMENT_LENGTH = 512;

const encode = (part: string): string => encodeURIComponent(part);
const decode = (part: string): string => decodeURIComponent(part);

const isNonEmpty = (s: string | undefined | null): s is string =>
  typeof s === 'string' && s.length > 0;

export function serialize(stack: CascadeStack): string {
  return stack
    .map((col) => {
      let seg = `${encode(col.type)}${TYPE_ID_SEPARATOR}${encode(col.id)}`;
      if (isNonEmpty(col.tab)) {
        seg += `${TAB_SEPARATOR}${encode(col.tab)}`;
      }
      if (col.parent !== undefined) {
        seg += `${PARENT_SEPARATOR}${col.parent}`;
        if (col.sourceListKey !== undefined) {
          seg += `${SLOT_SEPARATOR}${encode(col.sourceListKey)}`;
        }
      }
      return seg;
    })
    .join(SEGMENT_SEPARATOR);
}

interface ParsedSegment {
  type: string;
  id: string;
  tab?: string;
  /** Raw (undecoded, digits-only) parent-index tag, present iff a `^` tag existed. */
  parentRaw?: string;
  sourceListKey?: string;
}

/** Index of the first tag delimiter in `s`, or `s.length` when there is none. */
function firstDelimiter(s: string): number {
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === TAB_SEPARATOR || c === PARENT_SEPARATOR || c === SLOT_SEPARATOR) {
      return i;
    }
  }
  return s.length;
}

/** Parse one raw segment into its parts, or `null` when malformed. */
function parseSegment(segment: string): ParsedSegment | null {
  if (segment.length === 0 || segment.length > MAX_SEGMENT_LENGTH) {
    return null;
  }
  const colonIdx = segment.indexOf(TYPE_ID_SEPARATOR);
  if (colonIdx <= 0) {
    return null;
  }
  const typeRaw = segment.slice(0, colonIdx);
  let rest = segment.slice(colonIdx + 1);

  const idEnd = firstDelimiter(rest);
  const idRaw = rest.slice(0, idEnd);
  if (idRaw.length === 0) {
    return null;
  }
  rest = rest.slice(idEnd);

  let tabRaw: string | undefined;
  let parentRaw: string | undefined;
  let slotRaw: string | undefined;
  let hasSlot = false;

  while (rest.length > 0) {
    const marker = rest[0];
    const body = rest.slice(1);
    const end = firstDelimiter(body);
    const value = body.slice(0, end);
    rest = body.slice(end);
    if (marker === TAB_SEPARATOR) {
      if (tabRaw !== undefined || value.length === 0) {
        return null;
      }
      tabRaw = value;
    } else if (marker === PARENT_SEPARATOR) {
      if (parentRaw !== undefined || !/^\d+$/.test(value)) {
        return null;
      }
      parentRaw = value;
    } else if (marker === SLOT_SEPARATOR) {
      // A slot key only makes sense hanging off a parent tag.
      if (parentRaw === undefined || hasSlot) {
        return null;
      }
      hasSlot = true;
      slotRaw = value;
    } else {
      return null;
    }
  }

  try {
    const parsed: ParsedSegment = { type: decode(typeRaw), id: decode(idRaw) };
    if (tabRaw !== undefined) {
      parsed.tab = decode(tabRaw);
    }
    if (parentRaw !== undefined) {
      parsed.parentRaw = parentRaw;
    }
    if (hasSlot) {
      parsed.sourceListKey = decode(slotRaw!);
    }
    return parsed;
  } catch {
    return null;
  }
}

const toColumn = (parsed: ParsedSegment): CascadeColumn => {
  const col: CascadeColumn = { type: parsed.type, id: parsed.id };
  if (parsed.tab !== undefined) {
    col.tab = parsed.tab;
  }
  return col;
};

/** Legacy flat link (or a lone root): read as a single linear spine. */
function parseSpine(segments: string[]): CascadeColumn[] {
  const out: CascadeColumn[] = [];
  for (let i = 0; i < segments.length && i < MAX_SEGMENTS; i++) {
    const parsed = parseSegment(segments[i]);
    if (parsed === null) {
      break;
    }
    const col = toColumn(parsed);
    if (i > 0) {
      col.parent = i - 1;
    }
    out.push(col);
  }
  return out;
}

/** New tree grammar: every non-root segment references its parent by index. */
function parseTree(segments: string[]): CascadeColumn[] {
  const out: CascadeColumn[] = [];
  for (let i = 0; i < segments.length && i < MAX_SEGMENTS; i++) {
    const parsed = parseSegment(segments[i]);
    if (parsed === null) {
      break;
    }
    const col = toColumn(parsed);
    if (i === 0) {
      // The root must not carry a parent tag.
      if (parsed.parentRaw !== undefined) {
        return [];
      }
    } else {
      // A non-root segment must reference an already-seen earlier parent.
      if (parsed.parentRaw === undefined) {
        break;
      }
      const parent = Number(parsed.parentRaw);
      if (!Number.isInteger(parent) || parent < 0 || parent >= i) {
        break;
      }
      col.parent = parent;
      if (parsed.sourceListKey !== undefined) {
        col.sourceListKey = parsed.sourceListKey;
      }
    }
    out.push(col);
  }
  return out;
}

export function parse(serialized: string | undefined | null): CascadeColumn[] {
  if (!isNonEmpty(serialized)) {
    return [];
  }
  const segments = serialized.split(SEGMENT_SEPARATOR);
  // A raw `^` only ever appears as our parent-tag delimiter (it is escaped in
  // any value), so its presence anywhere marks the new tree grammar.
  const hasTreeTags = segments.some((s) => s.includes(PARENT_SEPARATOR));
  return hasTreeTags ? parseTree(segments) : parseSpine(segments);
}
